import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { NOTIFICATIONS_TYPES } from 'infrastructure/notification/constants';
import { setNotificationResponse } from 'infrastructure/notification/redux/actions';
import { AVAILABILITY_WS_EVENTS } from '../../../socket/constants';
import { subscribeAvailabilitySocketEvent } from '../../../socket/events';
import {
  InterruptionCancelledPayload,
  InterruptionDto,
  InterruptionEmployeeResponseStatus,
  InterruptionResponseStatusChangedPayload,
  InterruptionWireDto,
} from '../../types';
import { normalizeInterruption, normalizeInterruptions } from '../../shared/helpers/socketInterruptionMappers';
import {
  getInitialEmergencyInterruptions,
  getInitialListInterruptions,
  hasRequiredEmails,
  isIncomingInterruptionTracked,
  shouldAddEmergencyInterruption,
  shouldAddIncomingInterruptionToList,
} from '../../shared/helpers/eligibility';

type UseInterruptionsListenersParams = Readonly<{
  addEmergencyInterruption: (item: InterruptionDto) => void;
  addInterruption: (item: InterruptionDto) => void;
  currentUserId: string;
  enabled: boolean;
  emergencyInterruptions: InterruptionDto[];
  listInterruptions: InterruptionDto[];
  removeEmergencyInterruption: (id: string) => void;
  removeInterruption: (id: string) => void;
  mergeEmergencyInterruptionsSuccess: (items: InterruptionDto[]) => void;
  setInterruptionResponseStatus: (id: string, employeeResponseStatus: InterruptionEmployeeResponseStatus) => void;
  setInterruptionsSuccess: (items: InterruptionDto[]) => void;
}>;

const shouldKeepInterruptionAfterStatusUpdate = (status: InterruptionEmployeeResponseStatus) =>
  status === InterruptionEmployeeResponseStatus.New;

const useInterruptionsListeners = ({
  addEmergencyInterruption,
  addInterruption,
  currentUserId,
  enabled,
  emergencyInterruptions,
  listInterruptions,
  removeEmergencyInterruption,
  removeInterruption,
  mergeEmergencyInterruptionsSuccess,
  setInterruptionResponseStatus,
  setInterruptionsSuccess,
}: UseInterruptionsListenersParams) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!enabled || !currentUserId) {
      return undefined;
    }

    const setNotificationCount = (count: number) => {
      dispatch(
        setNotificationResponse({
          response: { count: Math.max(count, 0) },
          type: NOTIFICATIONS_TYPES.INTERRUPTION,
        }),
      );
    };

    const removeTrackedInterruption = (id: string) => {
      removeInterruption(id);
      removeEmergencyInterruption(id);

      if (listInterruptions.some(item => item.id === id)) {
        setNotificationCount(listInterruptions.length - 1);
      }
    };

    const unsubscribeDelivered = subscribeAvailabilitySocketEvent<InterruptionWireDto>(
      AVAILABILITY_WS_EVENTS.INTERRUPTION_DELIVERED,
      payload => {
        const interruption = normalizeInterruption(payload);

        if (isIncomingInterruptionTracked(interruption.id, emergencyInterruptions, listInterruptions)) {
          return;
        }

        if (!hasRequiredEmails(interruption)) {
          return;
        }

        if (shouldAddEmergencyInterruption(interruption, currentUserId)) {
          addEmergencyInterruption(interruption);
          return;
        }

        if (shouldAddIncomingInterruptionToList(interruption, currentUserId)) {
          addInterruption(interruption);
          setNotificationCount(listInterruptions.length + 1);
        }
      },
    );

    const unsubscribeCancelled = subscribeAvailabilitySocketEvent<InterruptionCancelledPayload>(
      AVAILABILITY_WS_EVENTS.INTERRUPTION_CANCELLED,
      payload => {
        if (payload?.id) {
          removeTrackedInterruption(payload.id);
        }
      },
    );

    const unsubscribeResponseStatusChanged = subscribeAvailabilitySocketEvent<InterruptionResponseStatusChangedPayload>(
      AVAILABILITY_WS_EVENTS.INTERRUPTION_RESPONSE_STATUS_CHANGED,
      payload => {
        if (!payload?.id || !payload.employeeResponseStatus) {
          return;
        }

        if (!shouldKeepInterruptionAfterStatusUpdate(payload.employeeResponseStatus)) {
          removeTrackedInterruption(payload.id);
          return;
        }

        setInterruptionResponseStatus(payload.id, payload.employeeResponseStatus);
      },
    );

    const unsubscribeInitialInterruptions = subscribeAvailabilitySocketEvent<InterruptionWireDto[]>(
      AVAILABILITY_WS_EVENTS.INITIAL_INTERRUPTION,
      payload => {
        const interruptions = normalizeInterruptions(Array.isArray(payload) ? payload : []);
        const listInterruptionsInit = getInitialListInterruptions(
          interruptions,
          emergencyInterruptions,
          [],
          currentUserId,
        );
        const emergencyInterruptionsInit = getInitialEmergencyInterruptions(
          interruptions,
          emergencyInterruptions,
          listInterruptions,
          currentUserId,
        );

        setInterruptionsSuccess(listInterruptionsInit);
        setNotificationCount(listInterruptionsInit.length);
        mergeEmergencyInterruptionsSuccess(emergencyInterruptionsInit);
      },
    );

    return () => {
      unsubscribeDelivered();
      unsubscribeCancelled();
      unsubscribeResponseStatusChanged();
      unsubscribeInitialInterruptions();
    };
  }, [
    addEmergencyInterruption,
    addInterruption,
    currentUserId,
    dispatch,
    enabled,
    emergencyInterruptions,
    listInterruptions,
    removeEmergencyInterruption,
    removeInterruption,
    mergeEmergencyInterruptionsSuccess,
    setInterruptionResponseStatus,
    setInterruptionsSuccess,
  ]);
};

export default useInterruptionsListeners;
